import React, { useEffect, useState } from 'react';
import { readUrlSecrets } from '../../utils/url-secret-scrub';
import { isTokenDeadBeyondSkew } from '../../utils/session';
import { takeGoogleSameTabSignIn } from '../../utils/google-same-tab-signin';
import { GOOGLE_POPUP_NOT_FINISHED, GOOGLE_POPUP_SIGNED_IN } from '../../utils/google-popup-result';
import { loginPathReturningTo, takeOAuthReturnPath } from '../../utils/oauth-return-path';
import {
    GOOGLE_CONNECT_ACK,
    GOOGLE_CONNECT_PROOF,
    openGoogleConnectChannel,
    takeGoogleConnectAttempt,
} from '../../utils/google-connect-handoff';

// Legitimate popup callers open /socialAuth via
// window.open(oauthUrl, "Sign In", …) — exam-review-drawer's in-context
// re-auth and Account Settings' Connect Google — so a genuine popup of ours
// always carries this window.name. The same-tab redirect flow sets no name.
const POPUP_WINDOW_NAME = 'Sign In';

// Google OAuth landing (/socialAuth?token=…). Two entry modes:
//   1. Popup (backward-compatible): opened by a caller that still uses
//      window.open — exam-review-drawer's in-context re-auth and Connect
//      Google. Hand the token back via postMessage and close. With no token
//      (the sign-in was cancelled or failed) say so instead, with no token,
//      so the opener stops waiting.
//   2. Same-tab redirect (E3): the login page navigates the whole tab to the
//      provider, so there is no popup context on return. Stash the token + a
//      pending flag and bounce to /login, which finishes the sign-in through
//      its existing, tested resolution + error surfaces.
//
// SECURITY (gate B1): the popup branch is taken ONLY for windows we opened
// ourselves. A bare `window.opener` check is not enough — if a foreign page
// window.open()'d an app URL, `opener` persists across the entire same-tab
// OAuth redirect chain, and posting the JWT to it with a '*' targetOrigin
// would hand the session token to an arbitrary origin and window.close() the
// user's sign-in. So: (a) require our known popup window.name in addition to
// an opener, and (b) lock postMessage's targetOrigin to our own origin —
// never '*' (the only legitimate consumer is same-origin; a cross-origin
// opener never receives the message even if it spoofs the name).
//
// Any other context reaches the same-tab branch, which REPLACES the stored
// session with the returned token. That is allowed only for the tab that
// started a same-tab sign-in on /login (google-same-tab-signin), whether or
// not a session is stored. Without that rule a /socialAuth link carrying
// someone else's token signed a signed-out visitor in as that account (login
// CSRF, 2026-10-18). Only /login, in its own tab, and the exam-review
// drawer, in the popup it opens, write the mark. A popup of ours that lost
// window.opener lands here too. That happens when its opener tab was closed before Google finished
// (Chromium keeps the window name; Firefox and WebKit clear it), or when a
// page in the sign-in sent an enforced Cross-Origin-Opener-Policy (name
// cleared in all three). A Connect Google popup's token belongs to the
// account being connected, and storing it signed the user in as that account
// (verified in all three engines, 2026-10-16), so such a landing stores
// nothing and says the account did not change.
// "Live" picks the wording of the refusal, with the skew-tolerant test: a
// fast device clock must not tell a signed-in student they are signed out.
//
// A Connect Google popup carries its own mark (google-connect-handoff). Its
// token is never stored, whatever the stored session's state. If it lost its
// opener it hands the proof to the Settings tab that opened it over a
// BroadcastChannel, and says so when no Settings tab takes it.
const HANDOFF_ACK_TIMEOUT_MS = 5000;

// The page's one action. At least 44px tall so it is easy to tap on a phone.
const PRIMARY_LINK_STYLE = { display: 'inline-flex', alignItems: 'center', minHeight: 44 };

// Posts the proof for the Settings tab whose attempt carries this nonce and
// waits for its ack. Returns the effect cleanup.
const handOffConnectProof = (nonce, token, settle) => {
    const channel = nonce ? openGoogleConnectChannel() : null;
    if (!channel) {
        settle('not-connected');
        return () => {};
    }
    let timer;
    const done = (outcome) => {
        clearTimeout(timer);
        channel.close();
        settle(outcome);
        if (outcome === 'handed-off') setTimeout(() => { window.close(); }, 300);
    };
    channel.onmessage = ({ data }) => {
        if (data?.type === GOOGLE_CONNECT_ACK && data.nonce === nonce) done('handed-off');
    };
    timer = setTimeout(() => done('no-reply'), HANDOFF_ACK_TIMEOUT_MS);
    channel.postMessage({ type: GOOGLE_CONNECT_PROOF, nonce, accessToken: token });
    return () => {
        clearTimeout(timer);
        channel.close();
    };
};

export const AuthRedirect = () => {
    // 'kept': a live session stays as it was. 'signed-out': nothing was
    // stored and no session is live.
    const [refused, setRefused] = useState(null);
    const [connectOutcome, setConnectOutcome] = useState(null);
    // Set when the sign-in did not finish: a same-tab landing with no token
    // (the Google sign-in was cancelled or failed, and the API sent the user
    // here with ?error=authentication_failed), or a popup whose hand-off to its
    // opener threw. It is where "Try again" goes.
    const [retryPath, setRetryPath] = useState(null);

    useEffect(() => {
        // F1: the head script in public/index.html normally stashed the token
        // and cleaned the URL before any tracking script ran. Read the stash;
        // if the token is still in the query (the head script could not stash),
        // readUrlSecrets scrubs it NOW, before the popup hand-off or the /login
        // navigation can make this URL the next document's referrer.
        const token = readUrlSecrets('/socialAuth', ['token']).values.token;

        if (window.opener && window.name === POPUP_WINDOW_NAME) {
            try {
                window.opener.postMessage(
                    token
                        ? { msg: GOOGLE_POPUP_SIGNED_IN, results: { accessToken: token } }
                        : { msg: GOOGLE_POPUP_NOT_FINISHED },
                    window.location.origin);
                setTimeout(() => { window.close(); }, 300);
            } catch (err) {
                // Nothing reached the opener, so the sign-in did not finish.
                setRetryPath('/login');
            }
            return;
        }

        // Consumed on every same-tab landing: one mark per round trip.
        const connectAttempt = takeGoogleConnectAttempt();
        const startedHere = takeGoogleSameTabSignIn();
        if (!token) {
            if (connectAttempt) {
                // A Connect Google popup that lost its opener: its user may be
                // signed in and is not signing in here, so send them back to
                // Settings. Drop the window name as below.
                window.name = '';
                setConnectOutcome('not-connected');
            } else {
                // Back to /login and, when this tab started the sign-in there,
                // on to the page it was for (/login's ?next= is gone by now).
                setRetryPath(loginPathReturningTo(startedHere ? takeOAuthReturnPath() : null));
            }
            return;
        }
        if (connectAttempt) {
            // Chromium keeps the popup name when the Settings tab is closed. Drop
            // it, or Connect Google from this window would reuse this window.
            window.name = '';
            if (!connectAttempt.fresh) {
                setConnectOutcome('not-connected');
                return;
            }
            return handOffConnectProof(connectAttempt.nonce, token, setConnectOutcome);
        }
        if (!startedHere) {
            setRefused(isTokenDeadBeyondSkew(window.localStorage.getItem('token')) ? 'signed-out' : 'kept');
            return;
        }
        window.localStorage.setItem('token', token);
        window.localStorage.setItem('oauthPending', 'true');
        window.location.replace('/login');
    }, []);

    if (connectOutcome === 'handed-off') {
        return (
            <main style={{ maxWidth: 480, margin: '48px auto', padding: '0 16px' }}>
                <h1 style={{ fontSize: 20 }}>Continue in QLearn Settings</h1>
                <p>Your Google account is ready to review in QLearn Settings. You can close this window.</p>
            </main>
        );
    }
    // No hand-off was possible (no channel or nonce, or a mark past the
    // Settings timeout): nothing is waiting in Settings.
    if (connectOutcome === 'not-connected') {
        return (
            <main style={{ maxWidth: 480, margin: '48px auto', padding: '0 16px' }}>
                <h1 style={{ fontSize: 20 }}>Google wasn't connected</h1>
                <p>Your QLearn account didn't change. Go back to Settings and choose Connect Google to try again.</p>
                <p><a href="/user-settings#account-sign-in">Go to Settings</a></p>
            </main>
        );
    }
    // No reply in time. A Settings tab asleep in the background may still take
    // the proof when it wakes, so this must not say the connection failed.
    if (connectOutcome === 'no-reply') {
        return (
            <main style={{ maxWidth: 480, margin: '48px auto', padding: '0 16px' }}>
                <h1 style={{ fontSize: 20 }}>Finish in QLearn Settings</h1>
                <p>Your QLearn account didn't change. If Settings doesn't show your Google account to review, choose Connect Google there to try again.</p>
                <p><a href="/user-settings#account-sign-in">Go to Settings</a></p>
            </main>
        );
    }
    // Shown in a popup or a whole tab, so no "close this window".
    if (refused === 'kept') {
        return (
            <main style={{ maxWidth: 480, margin: '48px auto', padding: '0 16px' }}>
                <h1 style={{ fontSize: 20 }}>Your QLearn account didn't change</h1>
                <p>QLearn didn't use this Google sign-in, so nothing about your account changed.</p>
                <p>Go back to QLearn and try again from the page where you started. If it happens again, <a href="/support">contact support</a>.</p>
                <p><a href="/">Go to QLearn</a></p>
            </main>
        );
    }
    if (refused === 'signed-out') {
        return (
            <main style={{ maxWidth: 480, margin: '48px auto', padding: '0 16px' }}>
                <h1 style={{ fontSize: 20 }}>Sign in from QLearn</h1>
                <p>QLearn didn't use this Google sign-in because it wasn't started from QLearn's sign-in page in this tab. You're not signed in.</p>
                <p><a href="/login">Sign in to QLearn</a></p>
            </main>
        );
    }
    // Signed in or not, /login is the way back: it sends a signed-in student
    // straight on.
    if (retryPath) {
        return (
            <main style={{ maxWidth: 480, margin: '48px auto', padding: '0 16px' }}>
                <h1 style={{ fontSize: 20 }}>Google sign-in didn&apos;t finish</h1>
                <p>You can try again, or choose another way to sign in. If it keeps happening, <a href="/support">contact support</a>.</p>
                <p><a href={retryPath} style={PRIMARY_LINK_STYLE}>Try again</a></p>
            </main>
        );
    }
    return (<></>);
}
